/**
 * AppLayout.
 *
 * The authenticated app shell: sidebar + main content area. Pages render
 * inside <Outlet />.
 *
 * Page-level outer padding is `px-8 py-7` per design language §4. Cards
 * and content live inside that padding.
 *
 * Two persistent overlays mount here so they're available on every
 * authenticated page:
 *
 *   - CopilotDrawer: the floating chat button + slide-out panel.
 *   - OnboardingDisclaimer: first-login legal/ethical gate before app use.
 *   - RoleOnboardingTour: 3-step role-specific intro after disclaimer (v4.3.9).
 *   - OnboardingWalkthrough: welcome modal on first login, then a
 *     persistent step panel that disappears once the user finishes
 *     or dismisses it.
 *   - OfflineBanner: connectivity + offline queue status above content.
 *
 * Pre-auth pages (LoginPage, ForgotPasswordPage, ResetPasswordPage) live
 * under AuthLayout instead and don't see these. The public WizardPage at
 * /apply also doesn't include them, by design - the applicant isn't a
 * tenant user.
 */
import { useState } from "react";

import { Outlet } from "react-router-dom";

import { OnboardingDisclaimer } from "@/features/auth/ui/OnboardingDisclaimer";
import { useBranding } from "@/features/branding/useBranding";
import { CopilotDrawer } from "@/features/copilot/ui/CopilotDrawer";
import { NotificationBell } from "@/features/notifications/ui/NotificationBell";
import { OnboardingWalkthrough } from "@/features/onboarding/ui/OnboardingWalkthrough";
import { RoleOnboardingTour } from "@/features/onboarding/ui/RoleOnboardingTour";
import { ClockControl } from "@/features/people/ui/ClockControl";
import { AccountMenu } from "@/widgets/account-menu/AccountMenu";
import { AppSidebar } from "@/widgets/app-sidebar/AppSidebar";
import { useSidebarCollapsed } from "@/widgets/app-sidebar/useSidebarCollapsed";
import { BreadcrumbProvider } from "@/widgets/breadcrumbs/BreadcrumbProvider";
import { Breadcrumbs } from "@/widgets/breadcrumbs/Breadcrumbs";

import { cn } from "@/shared/lib/cn";
import { OfflineBanner } from "@/shared/ui/OfflineBanner";

/** Authenticated app shell - sidebar plus the routed page Outlet. */
export function AppLayout() {
  useBranding();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { collapsed, toggleCollapsed } = useSidebarCollapsed();

  return (
    <BreadcrumbProvider>
      <div className="min-h-screen bg-background">
        {/* Audit fix A11Y1 - skip link: lets keyboard/screen-reader users
          jump past the sidebar nav to the page content. Visually hidden
          until focused. */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-white focus:px-4 focus:py-2 focus:shadow"
        >
          Skip to main content
        </a>

        {/* F-30: Mobile top bar with hamburger (hidden on lg+) */}
        <div className="fixed top-0 left-0 right-0 z-40 flex h-12 items-center justify-between gap-3 border-b border-border bg-background px-4 lg:hidden">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-1 text-foreground"
              aria-label="Toggle navigation"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 12h18M3 6h18M3 18h18" />
              </svg>
            </button>
            <span className="text-sm font-semibold">TNN</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <ClockControl />
            <AccountMenu compact />
            {/* Notifications commented out for now — do not remove.
          <NotificationBell />
          */}
          </div>
        </div>

        {sidebarOpen && (
          <div
            className="tnn-overlay fixed inset-0 z-40 bg-black/40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <AppSidebar
          mobileOpen={sidebarOpen}
          onMobileClose={() => setSidebarOpen(false)}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
        />
        <main
          id="main-content"
          className={cn(
            "mt-12 min-h-screen lg:mt-0 lg:transition-[margin] lg:duration-200",
            collapsed ? "lg:ml-16" : "lg:ml-60",
          )}
        >
          {/* The bell used to be `fixed right-4 top-3`, floating over whatever
            each page put in its own top-right corner. Every page then had to
            know about it and pad around it, and any page that forgot got its
            buttons overlapped. Here it occupies a row of its own, aligned to
            the same column as the content below it, so no page has to
            compensate and it stays put while the page scrolls. */}
          <div className="sticky top-0 z-30 hidden border-b border-border/60 bg-background/85 backdrop-blur lg:block">
            <div className="mx-auto flex md:h-14 h-12 max-w-350 items-center justify-between gap-3 px-4 sm:px-8">
              <div>
                <Breadcrumbs />
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {/* The clock lives here rather than on a page: you do not navigate
                somewhere to start working. Renders nothing for anyone with no
                program to track against. */}
                <ClockControl />
                <NotificationBell />
                <AccountMenu />
              </div>
            </div>
          </div>
          <div className="border-b border-border/60 px-4 py-2 lg:hidden">
            <Breadcrumbs />
          </div>
          <div className="mx-auto max-w-350 px-4 py-5 sm:px-8 sm:py-7">
            <OfflineBanner />
            <Outlet />
          </div>
        </main>
        <CopilotDrawer />
        <OnboardingDisclaimer />
        <RoleOnboardingTour />
        <OnboardingWalkthrough />
      </div>
    </BreadcrumbProvider>
  );
}
